import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, MapPin, Loader2, CreditCard, AlertCircle, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const BulkLocationPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [locations, setLocations] = useState([]);
  const locationIds = location.state?.locationIds || [];

  useEffect(() => {
    if (locationIds.length === 0) {
      toast.error('Keine Standorte zum Bezahlen');
      navigate('/admin/locations');
      return;
    }
    fetchLocations();
  }, [locationIds]);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter locations by IDs
      const filteredLocations = response.data.filter(loc => 
        locationIds.includes(loc.id)
      );
      
      setLocations(filteredLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaying(true);
    try {
      const token = localStorage.getItem('token');
      const frontend_url = window.location.origin;
      
      const response = await axios.post(
        `${API}/api/admin/locations/bulk-pay`,
        { 
          location_ids: locationIds,
          frontend_url 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating bulk payment session:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Zahlungssitzung');
      setPaying(false);
    }
  };

  const handlePayLater = () => {
    toast.info('Sie können die Standorte später unter "Standorte" bezahlen');
    navigate('/admin/locations');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (locations.length === 0) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p>Keine Standorte gefunden</p>
        </div>
      </DashboardLayout>
    );
  }

  const netPrice = locations.length * 14.99;
  const vatAmount = netPrice * 0.19;
  const grossPrice = netPrice + vatAmount;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="space-y-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/locations')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Standorten
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">
                {locations.length} Standorte aktivieren
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Bezahlen Sie alle Standorte zusammen
              </p>
            </div>
          </div>
        </div>

        {/* Info Alert */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-100">
            <p className="font-semibold mb-1">Bulk-Zahlung</p>
            <p>
              Sie bezahlen für {locations.length} Standorte auf einmal. 
              Nach erfolgreicher Zahlung können Sie sofort alle Module aktivieren.
            </p>
          </div>
        </div>

        {/* Locations List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Ihre neuen Standorte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {locations.map((loc, index) => (
              <div 
                key={loc.id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Badge variant="outline">{index + 1}</Badge>
                  <div>
                    <p className="font-medium">{loc.name}</p>
                    {loc.address && (
                      <p className="text-sm text-muted-foreground">{loc.address}</p>
                    )}
                  </div>
                </div>
                <p className="text-sm font-medium">14,99€/Monat</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>💶 Preisübersicht</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {locations.length} × Standort (14,99€/Monat)
                </span>
                <span className="font-medium">{netPrice.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">MwSt. (19%)</span>
                <span className="font-medium">{vatAmount.toFixed(2)}€</span>
              </div>
              <div className="h-px bg-border my-2"></div>
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamt</span>
                <span className="text-primary">{grossPrice.toFixed(2)}€/Monat</span>
              </div>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                ✅ <span className="font-semibold">Starter: 29,90 € Netto / 35,58 € Brutto / Monat</span>
                <br />
                15 Mitarbeiter inklusive · Monatlich kündbar
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handlePayment}
            disabled={paying}
            className="flex-1"
            size="lg"
          >
            {paying ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Weiterleitung...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Alle jetzt bezahlen ({grossPrice.toFixed(2)}€)
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={handlePayLater}
            disabled={paying}
            className="flex-1"
            size="lg"
          >
            Später bezahlen
          </Button>
        </div>

        {/* Security Note */}
        <p className="text-xs text-center text-muted-foreground">
          🔒 Sichere Zahlung über Stripe. Ihre Zahlungsinformationen werden verschlüsselt übertragen.
        </p>
      </div>
    </DashboardLayout>
  );
};
